

export let colUserNum = {
    colId: 'userNum',
    headerName: 'ID',
    field: 'email',  // Users are identified by email
    width: 200,
    sortable: true
};

export let colUserFullName = {
    colId: 'userFullName',
    headerName: 'Full Name',
    width: 200,
    sortable: true,
    valueGetter: (params: any) => {
        // Try different name formats
        if (params.data?.userFullName) return params.data.userFullName;
        if (params.data?.friendlyName) return params.data.friendlyName;
        if (params.data?.shortFriendlyName) return params.data.shortFriendlyName;
        // Check firstName/lastName (Profile fields) before legacy given_name/family_name
        if (params.data?.firstName && params.data?.lastName) {
            return `${params.data.lastName}, ${params.data.firstName}`;
        }
        if (params.data?.given_name && params.data?.family_name) {
            return `${params.data.family_name}, ${params.data.given_name}`;
        }
        return '';
    }
};

export let colRole = {
    colId: 'role',
    headerName: 'Role',
    field: 'role',
    width: 100,
    sortable: true
};

/**
 * Permission scoping (legal entities / contract types / database organizations /
 * departments / sites) is written by the Permissions screen into
 * `userData.userData.*`, but older/other writers put it at the root. Read both so
 * a column works regardless of which writer produced the record.
 */
function permissionList(params: any, field: string): any[] {
    const data = params.data;
    const value = data?.[field] ?? data?.userData?.userData?.[field] ?? data?.userData?.[field];
    return Array.isArray(value) ? value : (value ? [value] : []);
}

/**
 * Renders one entry of a scoping list. The backend returns these arrays verbatim
 * from `{org}-Users`, so entries are heterogeneous: a plain string, an object
 * carrying a display name, a doubly-wrapped object, or an id-only object. Falls
 * back through the known name keys, then the id, and finally renders blank —
 * never `[object Object]`.
 *
 * Uses `??` (not `||`) so a legitimate id of 0 survives instead of reading as
 * "missing".
 */
function entryLabel(entry: any): string {
    if (entry === null || entry === undefined) return '';
    if (typeof entry !== 'object') return String(entry);
    const label =
        entry.legalEntityName ??
        entry.legalEntityData?.legalEntityName ??
        entry.name ??
        entry.siteName ??
        entry.label ??
        entry.legalEntityNum ??
        entry.siteNum;
    return label === null || label === undefined ? '' : String(label);
}

/** Root-or-nested read + heterogeneous-entry rendering, comma-joined. */
function renderList(params: any, field: string): string {
    return permissionList(params, field).map(entryLabel).filter(Boolean).join(', ');
}

export let colDepartments = {
    colId: 'departments',
    headerName: 'Departments',
    width: 150,
    sortable: true,
    valueGetter: (params: any) => renderList(params, 'departments')
};

export let colSites = {
    colId: 'sites',
    headerName: 'Sites',
    width: 150,
    sortable: true,
    // Entries may be full Site objects (`name`) or bare siteNum strings — the
    // Permissions screen stores the raw string when the site isn't in its cache.
    valueGetter: (params: any) => renderList(params, 'sites')
};

export let colEmail = {
    colId: 'email',
    headerName: 'Email',
    field: 'email',
    width: 150,
    sortable: true
};

export let colLegalEntities = {
    colId: 'legalEntities',
    headerName: 'Legal Entities',
    width: 200,
    sortable: true,
    // Entries may be legal-entity objects, `legalEntityData`-wrapped objects,
    // id-only objects, or bare strings — see entryLabel().
    valueGetter: (params: any) => renderList(params, 'legalEntities')
};

export let colContractTypes = {
    colId: 'contractTypes',
    headerName: 'Contract Types',
    width: 200,
    sortable: true,
    valueGetter: (params: any) => {
        // `contractTypePermission` is the legacy name for the same list.
        const list = permissionList(params, 'contractTypes');
        const source = list.length ? list : permissionList(params, 'contractTypePermission');
        return source.map(entryLabel).filter(Boolean).join(', ');
    }
};

export let colDatabaseOrganizations = {
    colId: 'databaseOrganizations',
    headerName: 'Database Organizations',
    width: 200,
    sortable: true,
    valueGetter: (params: any) => renderList(params, 'databaseOrganizations')
};

export let colLastLogin = {
    colId: 'lastLogin',
    headerName: 'Last Login',
    field: 'lastLogin',
    width: 150,
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
        const raw = params.data?.lastLogin;
        if (!raw) return '';
        const d = new Date(raw);
        return isNaN(d.getTime()) ? '' : d;
    },
    valueFormatter: (params: any) =>
        params.value instanceof Date ? params.value.toLocaleString() : ''
};


export let allColumns = [
    colUserNum,
    colUserFullName,
    colRole,
    colDepartments,
    colSites,
    colEmail,
    colLegalEntities,
    colContractTypes,
    colDatabaseOrganizations,
    colLastLogin
];


export let columnSelectList = [
    {
        value: colUserNum,
        viewValue: colUserNum.headerName
    },
    {
        value: colUserFullName,
        viewValue: colUserFullName.headerName
    },
    {
        value: colRole,
        viewValue: colRole.headerName
    },
    {
        value: colDepartments,
        viewValue: colDepartments.headerName
    },
    {
        value: colSites,
        viewValue: colSites.headerName
    },
    {
        value: colEmail,
        viewValue: colEmail.headerName
    },
    {
        value: colLegalEntities,
        viewValue: colLegalEntities.headerName
    },
    {
        value: colContractTypes,
        viewValue: colContractTypes.headerName
    },
    {
        value: colDatabaseOrganizations,
        viewValue: colDatabaseOrganizations.headerName
    },
    {
        value: colLastLogin,
        viewValue: colLastLogin.headerName
    }
];
